import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Search,
  FileText,
  Truck,
  ReceiptText,
  ShieldAlert,
  ArrowRight,
  ArrowUpDown,
  Package,
  Layers,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Download,
  Loader2,
  CreditCard,
  Percent,
  Check,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import { SearchableSelect } from "../../components/ui/searchable-select";
import { EntityAvatar } from "../../components/ui/entity-avatar.tsx";
import { Chip } from "../../components/ui/chip";
import { cn } from "../../lib/utils";
import { getFinancialYear } from "../../lib/enhanced-currency-utils";
import apiService from "../../services/apiService";
import { Avatar, AvatarImage } from "../../components/ui/avatar.tsx";

type ComplianceReportType = "charge_filing" | "nach_mandate" | "tax_invoice_eway" | "gst_filing";

const COMPLIANCE_REPORT_OPTIONS: {
  value: ComplianceReportType;
  label: string;
  description: string;
  icon: typeof FileText;
}[] = [
  {
    value: "charge_filing",
    label: "Charge Filing",
    description: "CHG-1 status, SRN, filing dates and overdue ageing",
    icon: FileText,
  },
  {
    value: "nach_mandate",
    label: "NACH Mandate",
    description: "Mandate status, UMRN, bank and activation details",
    icon: CreditCard,
  },
  {
    value: "tax_invoice_eway",
    label: "Tax Invoice & E-Way Bill Status",
    description: "Per-PI tax invoice and e-way bill coverage",
    icon: Truck,
  },
  {
    value: "gst_filing",
    label: "GST Filing Status",
    description: "Vendor GSTIN, GSTR-1 reconciliation and ITC readiness",
    icon: Percent,
  },
];

function getDefaultComplianceExportDates() {
  const fy = getFinancialYear();
  const today = format(new Date(), "yyyy-MM-dd");
  return {
    fromDate: fy.start,
    toDate: today < fy.end ? today : fy.end,
  };
}

const PAGE_SIZE = 50;

type ApprovedNoaCustomer = { customerId: string; entityName: string };
type ApprovedNoaLender = { id: string; name: string };

type ApprovedNoaListRow = {
  noa_nach_active: boolean;
  disbursementDate: string;
  entityName: string;
  lenderName: string;
  displayId: string;
  mca_s3_file_url: string | null;
  lenderAvatar?: string | null;
  noaId?: string;
  scheduleId?: string;
  customerId?: string;
  lenderId?: string;
  rsId?: string;
  eway_count?: number;
  taxinvoiceCount?: number;
  shipmentCount?: number;
  totalAssetCount?: number;
  totalInsuranceCount?: number;
  customerImage?: string;
};

function getViewPath(row: ApprovedNoaListRow): string | null {
  const rsId = row.rsId;
  return rsId ? `/ViewPostDisbursement/${encodeURIComponent(rsId)}` : null;
}

function getRowKey(row: ApprovedNoaListRow, index: number): string {
  const stableId = row.noaId || row.scheduleId;
  if (stableId) {
    return row.displayId ? `${stableId}::${row.displayId}` : stableId;
  }
  if (row.displayId) {
    return `${row.displayId}::${row.disbursementDate}::${row.entityName}::${row.lenderName}::${index}`;
  }
  return `row-${index}`;
}

type ListPagination = {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
};

function pickNumber(...values: unknown[]): number | undefined {
  for (const v of values) {
    if (v === null || v === undefined || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function extractListItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  const obj = asRecord(payload);
  if (!obj) return [];
  for (const key of ["rows", "list", "cases", "items", "noaList", "records", "result"]) {
    const val = obj[key];
    if (Array.isArray(val)) return val;
  }
  const nested = obj.data;
  if (Array.isArray(nested)) return nested;
  return extractListItems(nested);
}

function parsePagination(
  sources: Record<string, unknown>[],
  requestedPage: number,
  rowsOnPage: number
): ListPagination {
  const flat = Object.assign({}, ...sources) as Record<string, unknown>;
  const pag = asRecord(flat.pagination) ?? {};
  const meta = asRecord(flat.meta) ?? {};

  const pageSize = pickNumber(pag.pageSize, pag.limit, meta.pageSize, meta.limit, flat.pageSize, flat.limit) ?? PAGE_SIZE;
  const currentPage =
    pickNumber(pag.currentPage, pag.page, meta.currentPage, meta.page, flat.currentPage, flat.page) ?? requestedPage;

  const totalCount = pickNumber(
    pag.totalCount,
    pag.totalRecords,
    pag.total,
    pag.count,
    meta.totalCount,
    meta.totalRecords,
    meta.total,
    meta.count,
    flat.totalCount,
    flat.totalRecords,
    flat.total,
    flat.count,
    flat.totalItems
  );

  let totalPages = pickNumber(pag.totalPages, pag.pageCount, meta.totalPages, flat.totalPages);
  const hasNextFromApi = [pag.hasNextPage, meta.hasNextPage, flat.hasNextPage].some(v => v === true);

  if (totalCount != null) {
    totalPages = totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));
  }

  const hasNextPage =
    hasNextFromApi ||
    (totalCount != null && currentPage * pageSize < totalCount) ||
    (totalPages != null && currentPage < totalPages) ||
    (totalCount == null && rowsOnPage >= pageSize);

  if (totalPages == null) {
    totalPages = hasNextPage ? Math.max(currentPage + 1, 2) : Math.max(1, currentPage);
  }

  const resolvedTotalCount =
    totalCount ?? (hasNextPage ? currentPage * pageSize + rowsOnPage : rowsOnPage);

  return {
    currentPage,
    pageSize,
    totalCount: resolvedTotalCount,
    totalPages: Math.max(1, totalPages),
    hasNextPage,
  };
}

function normalizeApprovedNoaRow(row: Record<string, unknown>): ApprovedNoaListRow {
  const noaId = String(row.noaId ?? row.noa_id ?? row.id ?? "").trim() || undefined;
  const scheduleId = String(row.scheduleId ?? row.schedule_id ?? row.rentalScheduleId ?? "").trim() || undefined;
  const displayId = String(row.displayId ?? row.display_id ?? row.rsId ?? row.rs_id ?? "").trim();
  const rsId = String(row.rsId);
  return {
    disbursementDate: String(row.disbursementDate ?? row.disbursement_date ?? ""),
    entityName: String(row.entityName ?? row.entity_name ?? ""),
    lenderName: String(row.lenderName ?? row.lender_name ?? ""),
    displayId,
    mca_s3_file_url: row.mca_s3_file_url != null ? String(row.mca_s3_file_url) : null,
    noa_nach_active: row.noa_nach_active != null ? Boolean(row.noa_nach_active) : false,
    noaId,
    scheduleId,
    customerId: row.customerId != null ? String(row.customerId) : undefined,
    lenderId: row.lenderId != null ? String(row.lenderId) : undefined,
    rsId,
    lenderAvatar: row.lenderAvatar != null ? String(row.lenderAvatar) : undefined,
    customerImage: row.customerImage != null ? String(row.customerImage) : undefined,
    eway_count: pickNumber(row.eway_count, row.ewayCount) ?? 0,
    taxinvoiceCount: pickNumber(row.taxinvoice_count, row.taxinvoiceCount) ?? 0,
    shipmentCount: pickNumber(row.shipmentCount, row.shipment_count) ?? 0,
    totalAssetCount: pickNumber(row.totalAssetCount, row.total_asset_count) ?? 0,
    totalInsuranceCount: pickNumber(row.totalInsuranceCount, row.total_insurance_count) ?? 0,
  };
}

function parseListResponse(
  response: unknown,
  requestedPage: number
): { rows: ApprovedNoaListRow[]; pagination: ListPagination; totalNoaCount?: number } {
  const res = asRecord(response) ?? {};
  const inner = res.data ?? response;
  const innerObj = asRecord(inner);
  const metaSources: Record<string, unknown>[] = [res];

  if (innerObj) {
    metaSources.push(innerObj);
  }

  const totalNoaCount = pickNumber(
    res.totalNoaCount,
    res.total_noa_count,
    innerObj?.totalNoaCount,
    innerObj?.total_noa_count
  );

  const allItems = extractListItems(inner ?? response);
  const allRows = allItems
    .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
    .map(normalizeApprovedNoaRow)
    .filter(r => r.displayId || r.noaId || r.scheduleId);

  let rows = allRows;
  let pagination: ListPagination;

  if (allRows.length > PAGE_SIZE) {
    const start = (requestedPage - 1) * PAGE_SIZE;
    rows = allRows.slice(start, start + PAGE_SIZE);
    const totalCount =
      pickNumber(
        asRecord(res.pagination)?.totalCount,
        asRecord(res.pagination)?.totalRecords,
        res.totalCount,
        res.totalRecords,
        res.total,
        res.count
      ) ?? allRows.length;
    pagination = {
      currentPage: requestedPage,
      pageSize: PAGE_SIZE,
      totalCount,
      totalPages: Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
      hasNextPage: start + PAGE_SIZE < totalCount,
    };
  } else {
    pagination = parsePagination(metaSources, requestedPage, allRows.length);
  }

  return { rows, pagination, totalNoaCount };
}

function parseDisbursementTime(value: string) {
  if (!value) return 0;
  try {
    return parseISO(value).getTime();
  } catch {
    return 0;
  }
}

function formatDisbursementDate(value: string) {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd MMM yyyy");
  } catch {
    return value;
  }
}

function PaginationBar({
  current,
  total,
  count,
  pageSize,
  hasNextPage,
  onPage,
}: {
  current: number;
  total: number;
  count: number;
  pageSize: number;
  hasNextPage: boolean;
  onPage: (p: number) => void;
}) {
  const from = count === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, count);
  const canGoNext = hasNextPage || current < total;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <span className="text-xs text-muted-foreground">
        Showing {from}–{to} of {count} records · Page {current} of {total}
      </span>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" disabled={current <= 1} onClick={() => onPage(current - 1)} className="h-8 w-8 p-0">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {Array.from({ length: total }, (_, i) => i + 1)
          .filter(p => p === 1 || p === total || Math.abs(p - current) <= 1)
          .reduce<(number | "...")[]>((acc, p, idx, arr) => {
            if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
            acc.push(p);
            return acc;
          }, [])
          .map((p, i) =>
            p === "..." ? (
              <span key={`ellipsis-${i}`} className="px-1 text-xs text-muted-foreground">…</span>
            ) : (
              <Button
                key={`page-${p}`}
                variant={p === current ? "default" : "outline"}
                size="sm"
                onClick={() => onPage(p as number)}
                className="h-8 w-8 p-0 text-xs"
              >
                {p}
              </Button>
            )
          )}
        <Button variant="outline" size="sm" disabled={!canGoNext} onClick={() => onPage(current + 1)} className="h-8 w-8 p-0">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

type FilterKey = "all" | "ewb" | "ti" | "charge" | "ins";

type PendingFilterKey = Exclude<FilterKey, "all">;

const PENDING_FILTER_SECTIONS: Record<PendingFilterKey, string> = {
  ewb: "eway",
  ti: "taxinvoice",
  charge: "mca",
  ins: "insurance",
};

type DashboardCounts = {
  totalAssetsValue: number;
  taxinvoicePending: number;
  ewayPending: number;
  mcaPending: number;
  insurancePending: number;
};

function extractDashboardCounts(response: unknown): DashboardCounts {
  const res = asRecord(response) ?? {};
  const data = asRecord(res.data) ?? res;
  return {
    totalAssetsValue: pickNumber(data.total_assets_value) ?? 0,
    taxinvoicePending: pickNumber(data.taxinvoice_pending) ?? 0,
    ewayPending: pickNumber(data.eway_pending) ?? 0,
    mcaPending: pickNumber(data.mca_pending) ?? 0,
    insurancePending: pickNumber(data.insurance_pending) ?? 0,
  };
}

function formatDashboardAssetValue(value: number) {
  return `₹${Math.round(value).toLocaleString("en-IN")}`;
}

function formatDashboardCount(value: number) {
  return value.toLocaleString("en-IN");
}

export default function PostDisbursement() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ApprovedNoaListRow[]>([]);
  const [isCasesLoading, setIsCasesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalNoaCount, setTotalNoaCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tab, setTab] = useState<FilterKey>("all");
  const [customerFilter, setCustomerFilter] = useState("");
  const [approvedCustomers, setApprovedCustomers] = useState<ApprovedNoaCustomer[]>([]);
  const [lenderFilter, setLenderFilter] = useState("");
  const [approvedLenders, setApprovedLenders] = useState<ApprovedNoaLender[]>([]);
  const [sortDesc, setSortDesc] = useState(true);
  const [dashboardCounts, setDashboardCounts] = useState<DashboardCounts | null>(null);
  const [dashboardCountsLoading, setDashboardCountsLoading] = useState(true);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState(() => getDefaultComplianceExportDates().fromDate);
  const [exportToDate, setExportToDate] = useState(() => getDefaultComplianceExportDates().toDate);
  const [exportReportType, setExportReportType] = useState<ComplianceReportType>("charge_filing");
  const [exportingReport, setExportingReport] = useState(false);

  const openExportDialog = () => {
    const defaults = getDefaultComplianceExportDates();
    setExportFromDate(defaults.fromDate);
    setExportToDate(defaults.toDate);
    setExportReportType("charge_filing");
    setExportDialogOpen(true);
  };

  const handleExportComplianceReport = async () => {
    if (!exportFromDate || !exportToDate) {
      toast.error("Please select From Date and To Date");
      return;
    }
    if (exportFromDate > exportToDate) {
      toast.error("From Date cannot be after To Date");
      return;
    }
    if (exportingReport) return;

    setExportingReport(true);
    try {
      const blob = await apiService.post<Blob>(
        "/disbursement/export-compliance-report",
        {
          fromDate: exportFromDate,
          toDate: exportToDate,
          reportType: exportReportType,
        },
        { responseType: "blob" },
      );

      const selected = COMPLIANCE_REPORT_OPTIONS.find((opt) => opt.value === exportReportType);
      const fileName = `compliance-${exportReportType}-${exportFromDate}-to-${exportToDate}-${format(new Date(), "yyyyMMdd-HHmm")}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selected?.label ?? "Compliance"} report downloaded`);
      setExportDialogOpen(false);
    } catch (error: any) {
      console.error("Failed to export compliance report:", error);
      toast.error(error?.message || "Failed to export compliance report");
    } finally {
      setExportingReport(false);
    }
  };

  const fetchApprovedNoaList = async (
    page: number,
    customerId?: string,
    lenderId?: string,
    filterSection?: string,
    search?: string
  ) => {
    const body: Record<string, unknown> = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (customerId) body.customerId = customerId;
    if (lenderId) body.lenderId = lenderId;
    if (filterSection) body.filterSection = filterSection;
    if (search) body.search = search;
    const response = await apiService.post<unknown>("/disbursement/get-approved-noa-list", body);
    return parseListResponse(response, page);
  };

  const searchRef = useRef(debouncedSearch);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = q.trim();
      if (next !== searchRef.current) {
        searchRef.current = next;
        setDebouncedSearch(next);
        setCurrentPage(1);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setIsCasesLoading(true);
      try {
        const filterSection =
          tab !== "all" ? PENDING_FILTER_SECTIONS[tab as PendingFilterKey] : undefined;
        const { rows: nextRows, pagination, totalNoaCount: nextTotalNoaCount } = await fetchApprovedNoaList(
          currentPage,
          customerFilter || undefined,
          lenderFilter || undefined,
          filterSection,
          debouncedSearch || undefined
        );
        if (!cancelled) {
          setRows(nextRows);
          setTotalCount(pagination.totalCount);
          setTotalPages(pagination.totalPages);
          setHasNextPage(pagination.hasNextPage);
          if (nextTotalNoaCount != null) setTotalNoaCount(nextTotalNoaCount);
        }
      } catch (e) {
        console.error("Error fetching approved NOA list:", e);
        if (!cancelled) {
          setRows([]);
          setTotalCount(0);
          setTotalPages(1);
          setHasNextPage(false);
        }
      } finally {
        if (!cancelled) setIsCasesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentPage, customerFilter, lenderFilter, tab, debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setDashboardCountsLoading(true);
      try {
        const response = await apiService.get<unknown>("/disbursement/get-dashboard-count");
        if (!cancelled) setDashboardCounts(extractDashboardCounts(response));
      } catch (e) {
        console.error("Error fetching dashboard counts:", e);
        if (!cancelled) setDashboardCounts(null);
      } finally {
        if (!cancelled) setDashboardCountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCustomerFilterChange = (value: string) => {
    setCustomerFilter(value);
    setCurrentPage(1);
  };

  const handleLenderFilterChange = (value: string) => {
    setLenderFilter(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await apiService.get<{ data?: unknown } | unknown>(
          "/disbursement/get-approved-noa-customers"
        );
        const payload = (response as { data?: unknown })?.data ?? response;
        const raw = Array.isArray(payload)
          ? payload
          : Array.isArray((payload as { customers?: unknown[] })?.customers)
            ? (payload as { customers: unknown[] }).customers
            : [];
        const mapped: ApprovedNoaCustomer[] = [];
        for (const row of raw) {
          if (!row || typeof row !== "object") continue;
          const r = row as Record<string, unknown>;
          const customerId = String(r.customerId ?? "").trim();
          const entityName = String(r.entityName ?? "").trim();
          if (customerId && entityName) mapped.push({ customerId, entityName });
        }
        mapped.sort((a, b) =>
          a.entityName.localeCompare(b.entityName, undefined, { sensitivity: "base" })
        );
        if (!cancelled) setApprovedCustomers(mapped);
      } catch (e) {
        console.error("Error fetching approved NOA customers:", e);
        if (!cancelled) setApprovedCustomers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await apiService.get<{ data?: unknown } | unknown>(
          "/disbursement/get-approved-noa-lenders"
        );
        const payload = (response as { data?: unknown })?.data ?? response;
        const raw = Array.isArray(payload)
          ? payload
          : Array.isArray((payload as { lenders?: unknown[] })?.lenders)
            ? (payload as { lenders: unknown[] }).lenders
            : [];
        const mapped: ApprovedNoaLender[] = [];
        for (const row of raw) {
          if (!row || typeof row !== "object") continue;
          const r = row as Record<string, unknown>;
          const id = String(r.id ?? "").trim();
          const name = String(r.name ?? "").trim();
          if (id && name) mapped.push({ id, name });
        }
        mapped.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        );
        if (!cancelled) setApprovedLenders(mapped);
      } catch (e) {
        console.error("Error fetching approved NOA lenders:", e);
        if (!cancelled) setApprovedLenders([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const customerOptions = useMemo(() => [
    { value: "", label: "All Customers" },
    ...approvedCustomers.map(c => ({ value: c.customerId, label: c.entityName })),
  ], [approvedCustomers]);

  const lenderOptions = useMemo(() => [
    { value: "", label: "All Lenders" },
    ...approvedLenders.map(l => ({ value: l.id, label: l.name })),
  ], [approvedLenders]);

  const filtered = useMemo(() => {
    const list = [...rows];
    list.sort((a, b) => {
      const da = parseDisbursementTime(a.disbursementDate);
      const db = parseDisbursementTime(b.disbursementDate);
      return sortDesc ? db - da : da - db;
    });

    return list;
  }, [rows, sortDesc]);

  const resetFilters = () => {
    searchRef.current = "";
    setQ("");
    setDebouncedSearch("");
    setCustomerFilter("");
    setLenderFilter("");
    setTab("all");
    setCurrentPage(1);
  };

  const handleTabChange = (key: FilterKey) => {
    setTab(key);
    setCurrentPage(1);
  };

  const ctas: Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    count: React.ReactNode;
    tone: string;
    filterKey?: FilterKey;
  }> = [
      { key: "all", filterKey: "all", label: "Total RS", icon: <Layers className="h-3.5 w-3.5" />, count: formatDashboardCount(totalNoaCount), tone: "text-foreground" },
      {
        key: "assets",
        label: "Total Assets",
        icon: <Package className="h-3.5 w-3.5" />,
        count: dashboardCountsLoading ? "—" : dashboardCounts ? formatDashboardAssetValue(dashboardCounts.totalAssetsValue) : "—",
        tone: "text-foreground",
      },
      {
        key: "ewb",
        filterKey: "ewb",
        label: "E-Way Bill Pending",
        icon: <Truck className="h-3.5 w-3.5" />,
        count: dashboardCountsLoading ? "—" : dashboardCounts ? formatDashboardCount(dashboardCounts.ewayPending) : "—",
        tone: "text-amber-700",
      },
      {
        key: "ti",
        filterKey: "ti",
        label: "Tax Invoice Pending",
        icon: <ReceiptText className="h-3.5 w-3.5" />,
        count: dashboardCountsLoading ? "—" : dashboardCounts ? formatDashboardCount(dashboardCounts.taxinvoicePending) : "—",
        tone: "text-amber-700",
      },
      {
        key: "charge",
        filterKey: "charge",
        label: "Charge Filing Pending",
        icon: <FileText className="h-3.5 w-3.5" />,
        count: dashboardCountsLoading ? "—" : dashboardCounts ? formatDashboardCount(dashboardCounts.mcaPending) : "—",
        tone: "text-red-700",
      },
      {
        key: "ins",
        filterKey: "ins",
        label: "Insurance Pending",
        icon: <ShieldAlert className="h-3.5 w-3.5" />,
        count: dashboardCountsLoading ? "—" : dashboardCounts ? formatDashboardCount(dashboardCounts.insurancePending) : "—",
        tone: "text-red-700",
      },
    ];

    function getInitials(text: string): string {
      if (!text || typeof text !== 'string') return '';
      const parts = text.trim().split(/\s+/).filter(Boolean);
      const letters =
        parts.length >= 2
          ? `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`
          : (parts[0]?.slice(0, 2) ?? '');
      return letters.toUpperCase();
    }

  return (
    <div className="space-y-5 w-full p-4 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Post-Disbursement Compliance</h1>
        <Button variant="outline" className="gap-2" onClick={openExportDialog}>
          <Download className="h-4 w-4" />
          Export
        </Button>
      </div>

      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Export Compliance Report
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="compliance-from-date">From Date</Label>
                  <Input
                    id="compliance-from-date"
                    type="date"
                    value={exportFromDate}
                    onChange={(e) => setExportFromDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="compliance-to-date">To Date</Label>
                  <Input
                    id="compliance-to-date"
                    type="date"
                    value={exportToDate}
                    onChange={(e) => setExportToDate(e.target.value)}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Period filters on disbursement date. Defaults to the current financial year.
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">What to download — select one report</p>
              <div className="space-y-2">
                {COMPLIANCE_REPORT_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const selected = exportReportType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setExportReportType(option.value)}
                      className={cn(
                        "w-full rounded-lg border px-3 py-3 text-left transition-all",
                        selected
                          ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                          : "border-border bg-background hover:bg-muted/40",
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={cn(
                            "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                            selected ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className={cn("text-sm font-medium", selected && "text-primary")}>
                            {option.label}
                          </div>
                          <div className="text-xs text-muted-foreground">{option.description}</div>
                        </div>
                        {selected && <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              Export respects the filters applied on screen. One CSV file for the selected report.
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setExportDialogOpen(false)} disabled={exportingReport}>
              Cancel
            </Button>
            <Button className="gap-2" onClick={() => void handleExportComplianceReport()} disabled={exportingReport}>
              {exportingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {ctas.map(c => {
          const active = c.filterKey != null && tab === c.filterKey;
          const cardContent = (
            <>
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                {c.icon}
                <span className="truncate">{c.label}</span>
              </div>
              <div className={cn("mt-1 flex items-baseline gap-1.5", c.tone)}>
                <span className="text-xl font-semibold tabular-nums">{c.count}</span>
              </div>
            </>
          );

          if (!c.filterKey) {
            return (
              <div
                key={c.key}
                className="rounded-lg border border-border px-3 py-2.5 text-left bg-background"
              >
                {cardContent}
              </div>
            );
          }

          return (
            <button
              key={c.key}
              type="button"
              onClick={() => handleTabChange(c.filterKey!)}
              className={cn(
                "rounded-lg border px-3 py-2.5 text-left transition-all bg-background hover:shadow-sm",
                active ? "border-primary ring-2 ring-primary/20 bg-primary/5" : "border-border"
              )}
            >
              {cardContent}
            </button>
          );
        })}
      </div>

      <div className="space-y-4">
        <Card>
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search customer..."
                className="pl-9"
              />
            </div>
            <div className="w-[200px]">
              <SearchableSelect
                options={customerOptions}
                value={customerFilter}
                onValueChange={handleCustomerFilterChange}
                placeholder="All Customers"
              />
            </div>
            <div className="w-[200px]">
              <SearchableSelect
                options={lenderOptions}
                value={lenderFilter}
                onValueChange={handleLenderFilterChange}
                placeholder="All Lenders"
              />
            </div>
            {(q || debouncedSearch || customerFilter || lenderFilter || tab !== "all") && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                Reset
              </Button>
            )}
            <div className="text-xs text-muted-foreground ml-auto">
              {filtered.length} on this page · {totalCount} total
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur">
                <TableRow>
                  <TableHead>
                    <button
                      type="button"
                      onClick={() => setSortDesc(!sortDesc)}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      Disbursed On <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Lender</TableHead>
                  <TableHead>RS No.</TableHead>
                  <TableHead className="text-center">MCA Charge</TableHead>
                  <TableHead className="text-center">NACH</TableHead>
                  <TableHead className="text-center">E-Way Bill</TableHead>
                  <TableHead className="text-center">Tax Invoice</TableHead>
                  <TableHead className="text-center">Insurance</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isCasesLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                      Loading cases…
                    </TableCell>
                  </TableRow>
                )}
                {!isCasesLoading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                      No cases match the current filters.
                    </TableCell>
                  </TableRow>
                )}
                {!isCasesLoading &&
                  filtered.map((c, index) => (
                    <TableRow
                      key={getRowKey(c, index)}
                      className="hover:bg-muted/40 cursor-pointer"
                      onClick={() => {
                        const path = getViewPath(c);
                        if (path) navigate(path);
                      }}
                    >
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDisbursementDate(c.disbursementDate)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                        {c.customerImage ? (
                        <img
                          src={c.customerImage}
                          alt={c.entityName}
                          className="w-7 h-7 rounded-md flex-shrink-0"
                        /> 
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-semibold text-muted-foreground">
                            {getInitials(c.entityName)}
                          </span>
                          </div>
                        )}
                        <span className="text-sm font-medium truncate">{c.entityName || "—"}</span> 
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.lenderName ? (
                          <div className="flex items-center gap-2">
                            <Avatar className="rounded-lg" style={{ height: "30px", width: "45px" }}>
                              <AvatarImage
                                src={
                                  c.lenderAvatar?.startsWith("http")
                                    ? c.lenderAvatar
                                    : `${import.meta.env.VITE_API_BASE_URL}/${c.lenderAvatar}`
                                }
                                alt={c.lenderName || "Lender"}
                                className="object-contain"
                              />
                            </Avatar>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">{c.lenderName || "—"}</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-sm">RS NO - {c.displayId}</TableCell>
                      <TableCell className="text-center">
                        {c.mca_s3_file_url !== null ? (
                          <Chip className="bg-emerald-500/10 text-emerald-700 text-[10px] px-2 py-0.5">
                            <CheckCircle2 className="h-3 w-3 mr-1" />Filed
                          </Chip>
                        ) : (
                          <Chip className="bg-blue-500/10 text-blue-700 text-[10px] px-2 py-0.5">Pending</Chip>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {c.noa_nach_active == true ? (
                          <Chip className="bg-emerald-500/10 text-emerald-700 text-[10px] px-2 py-0.5">
                            <CheckCircle2 className="h-3 w-3 mr-1" />Active
                          </Chip>
                        ) : (
                          <Chip className="bg-blue-500/10 text-blue-700 text-[10px] px-2 py-0.5">Pending</Chip>
                        )}
                      </TableCell>
                      <TableCell className="text-center tabular-nums text-sm">
                        {c.eway_count === 0 && c.shipmentCount === 0
                          ? "0"
                          : `${c.eway_count ?? 0}/${c.shipmentCount ?? 0}`}
                      </TableCell>
                      <TableCell className="text-center tabular-nums text-sm">
                        {c.taxinvoiceCount === 0 && c.shipmentCount === 0
                          ? "0"
                          : `${c.taxinvoiceCount ?? 0}/${c.shipmentCount ?? 0}`}
                      </TableCell>
                      <TableCell className="text-center tabular-nums text-sm">
                        {c.totalInsuranceCount === 0 && c.shipmentCount === 0
                          ? "0"
                          : `${c.totalInsuranceCount ?? 0}/${c.shipmentCount ?? 0}`}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            const path = getViewPath(c);
                            if (path) navigate(path);
                          }}
                        >
                          Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <PaginationBar
              current={currentPage}
              total={totalPages}
              count={totalCount}
              pageSize={PAGE_SIZE}
              hasNextPage={hasNextPage}
              onPage={setCurrentPage}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


